import React, { ComponentType, ForwardRefRenderFunction, forwardRef, InputHTMLAttributes, ReactNode } from 'react'

import { TFunctionDetailedResult } from 'i18next'
import { NumberFormatValues, NumericFormat, NumericFormatProps, SourceInfo } from 'react-number-format'

import { AdornmentPosition } from '@/models/common'

import { Input } from './atoms'

interface OwnProps {
  hasError?: boolean
  onValueChange?: (values: NumberFormatValues, sourceInfo?: SourceInfo) => void
  floatvalue?: number
  error?: boolean
  adornment?: ReactNode | TFunctionDetailedResult
  adornmentPosition?: AdornmentPosition
  customInput?: ComponentType
  withIconRight?: boolean
  decimalScale?: number
}

type Props = OwnProps & NumericFormatProps

const roundNumber = (num: number, scale: number): number => {
  const factor = Math.pow(10, scale)
  return Math.round(num * factor) / factor
}

type NFInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  type?: React.HTMLInputTypeAttribute
}

interface NumberFormatInputProps extends NFInputProps {
  adornment?: ReactNode
  adornmentPosition?: AdornmentPosition
}

const NumberFormatInput = forwardRef<HTMLInputElement, NumberFormatInputProps>(({ adornment, adornmentPosition, ...props }, ref) => (
  <Input ref={ref} {...props} adornment={adornment} adornmentPosition={adornmentPosition} type="text" />
))

const NumericInput: ForwardRefRenderFunction<Props> = (
  { hasError, onValueChange, id, allowNegative, value, customInput, decimalScale, adornment, adornmentPosition, ...rest }: Props,
  ref: any,
) => {
  const handleOnValueChange = (values: NumberFormatValues): void => {
    onValueChange && onValueChange(values)
  }

  const roundedValue = typeof value === 'number' && decimalScale !== undefined ? roundNumber(value, decimalScale) : value

  const CustomInputComponent = customInput || NumberFormatInput

  return (
    <NumericFormat
      id={id}
      getInputRef={ref}
      error={hasError ? hasError : undefined}
      allowNegative={allowNegative || false}
      onValueChange={handleOnValueChange}
      value={roundedValue}
      autoComplete="off"
      customInput={CustomInputComponent}
      // Pass these as custom props that will be forwarded to the custom input
      {...{ adornment, adornmentPosition }}
      {...rest}
    />
  )
}

NumericInput.displayName = 'NumericInput'

export default forwardRef<any, Props>(NumericInput)
